import { useCallback } from 'react'

import type { StudentGroupedDualListExtraProps } from '@/component/molecules/DualList/types'
import type { Student } from '@/dto'
import type DualListBoxV2 from '@/utils/models/DualListBoxV2'

import { useDualListState } from './useDualListState'
import { useStudentItems } from './useStudentItems'

type UseGroupedStudentDualListOptions = {
  /** 초기 선택된 학생 ID 목록 */
  initialRightItemIds?: Student['id'][]
  /** 로드된 학생에서 초기 right 항목을 동적으로 선택. 지정 시 initialRightItemIds 보다 우선한다 */
  initialRightItemFilter?: (item: Student) => boolean
  /** 학생 상태 필터 */
  studentStatus?: ('ACTIVE' | 'INACTIVE')[]
  /** 홈룸(담당 반) 권한을 무시하고 학원 전체 학생·반을 조회 */
  ignoreHomeroom?: boolean
} & Partial<StudentGroupedDualListExtraProps>

type UseGroupedStudentDualListReturn = {
  /** DualListBox 인스턴스 (null이면 준비 안됨) */
  dualListBox: DualListBoxV2<Student> | null
  /** 선택된 학생 목록 (right) */
  rightItems: Student[]
  /** 미선택 학생 목록 (left) */
  leftItems: Student[]
  /** 초기화 완료 여부 */
  isReady: boolean
  /** 데이터 로딩 중 여부 */
  isLoading: boolean
  /** 초기 상태 대비 변경 여부 */
  isChanged: boolean
  /** 초기 right → left로 이동한 학생 */
  movedToLeftItems: Student[]
  /** 초기 left → right로 이동한 학생 (새로 추가된 학생) */
  movedToRightItems: Student[]
  /** Right 전체를 Left로 이동 */
  moveAllToLeft: () => void
  /** StudentGroupedDualList에 전달할 추가 props */
  groupedDualListProps: StudentGroupedDualListExtraProps
}

/**
 * Student 데이터 fetch + 그룹핑된 DualList 상태 관리 훅
 * 학년/반별 그룹핑 UI를 제공
 *
 * @example
 * const { dualListBox, rightItems, isLoading, isReady, groupedDualListProps } = useGroupedStudentDualList({
 *   initialRightItemIds: [1, 2, 3],
 * })
 *
 * if (isLoading || !isReady) return <Loader />
 * return (
 *   <StudentGroupedDualList
 *     dualListBox={dualListBox}
 *     emptyPreset="student"
 *     {...groupedDualListProps}
 *   />
 * )
 */
export const useGroupedStudentDualList = (
  options: UseGroupedStudentDualListOptions = {},
): UseGroupedStudentDualListReturn => {
  const {
    initialRightItemIds = [],
    initialRightItemFilter,
    studentStatus,
    ignoreHomeroom,
    showTabs,
    defaultOpen,
    defaultGroupBy,
  } = options
  const { students, isLoading, studentLessonClassMap, lessonClassOrder, getLessonClassName } =
    useStudentItems({
      studentStatus,
      ignoreHomeroom,
    })
  // resolvedRightItemIds 는 매 렌더 새 배열이지만 useDualListState 가 값(문자열) 비교로
  // 박스 재생성을 가드하므로 재계산해도 안전하다.
  const resolvedRightItemIds = initialRightItemFilter
    ? students.filter(initialRightItemFilter).map(({ id }) => id)
    : initialRightItemIds
  const { dualListBox } = useDualListState({
    items: students,
    initialRightItemIds: resolvedRightItemIds,
  })

  const rightItems = dualListBox?.rightList.toSortArrByKey('name') ?? []
  const leftItems = dualListBox?.leftList.toSortArrByKey('name') ?? []
  const movedToLeftItems = dualListBox?.movedToLeftItems ?? []
  const movedToRightItems = dualListBox?.movedToRightItems ?? []
  const moveAllToLeft = useCallback(() => {
    dualListBox?.moveRightToLeftAll()
  }, [dualListBox])
  const groupedDualListProps: StudentGroupedDualListExtraProps = {
    showTabs,
    defaultOpen,
    defaultGroupBy,
    studentLessonClassMap,
    getLessonClassName,
    lessonClassOrder,
  }

  return {
    dualListBox,
    rightItems,
    leftItems,
    isReady: !!dualListBox,
    isLoading,
    isChanged: dualListBox?.isChanged ?? false,
    movedToLeftItems,
    movedToRightItems,
    moveAllToLeft,
    groupedDualListProps,
  }
}

export default useGroupedStudentDualList
